import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Filter, ChevronDown, MoreVertical, Shield,
  UserCheck, UserX, Mail, Phone, MapPin, Calendar,
  Download, Trash2, Edit, Eye, X, CheckCircle, AlertTriangle,
  Users, ArrowUpDown, Ban, Lock, Unlock, AlertCircle, Briefcase,
  User, MessageSquare, Star, Building2
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import { getAllUsers, useAppDispatch, suspendUser, activateUser, deleteUser, getFavoriteByUserId } from "../../redux";
import { useSelector } from "react-redux";
import { selectLoaderValue } from "../../redux/Slices/loadingSlice";
import Spinner from "../../modules/shared/components/Spinner";
import { EditUserDetailModal } from "./components";
import { StatusEnum } from "../../modules/shared/model";
import ProfessionalDetailModal from "./components/ProfessionalDetailModal";
import FavoriteButton from "../../components/ui/FavoriteButton";

// RealTimeFavoriteButton component for local professionals
interface RealTimeFavoriteButtonProps {
  professional: any;
  currentUserFavorite: any;
}

const RealTimeFavoriteButton = React.memo(({ professional, currentUserFavorite }: RealTimeFavoriteButtonProps) => {
  // Initialize with API response data first
  const [isFavorite, setIsFavorite] = useState(professional.favorite?.id ? true : false);

  // Listen for changes in currentUserFavorite to update the button state
  useEffect(() => {
    if (currentUserFavorite && currentUserFavorite.userIds) {
      const isFavorited = currentUserFavorite.userIds.some(favUser => favUser.id === professional.id.toString());
      setIsFavorite(isFavorited);
    } else {
      // If no favorites exist in Redux state, fall back to API response data
      setIsFavorite(professional.favorite?.id ? true : false);
    }
  }, [currentUserFavorite, professional.id, professional.favorite?.id]);

  return (
    <FavoriteButton
      targetUserId={professional.id.toString()}
      size="sm"
      className="p-1 rounded-full bg-white shadow-sm hover:bg-gray-50"
      favoriteData={{
        id: professional.favorite?.id,
        isFavorite: isFavorite
      }}
    />
  );
});

RealTimeFavoriteButton.displayName = "RealTimeFavoriteButton";

const LocalProfessionalsPage2 = () => {
  const [selectedProfessionals, setSelectedProfessionals] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [professionalType, setProfessionalType] = useState("all");
  const [status, setStatus] = useState(0);
  const [showProfessionalDetails, setShowProfessionalDetails] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [isSearching, setIsSearching] = useState(false);

  const dispatch = useAppDispatch();
  const isLoading = useSelector(selectLoaderValue);
  const { data: allUsers, totalCount, hasNextPage } = useSelector((state: any) => state.admin.allUsers);
  const { currentUserFavorite } = useSelector((state: any) => state.favorites);
  const { user } = useSelector((state: any) => state.auth);

  // Load user's favorites on mount
  useEffect(() => {
    if (user?.id && !currentUserFavorite) {
      dispatch(getFavoriteByUserId(user.id));
    }
  }, [user?.id, currentUserFavorite, dispatch]);

  // Initial load and page change
  useEffect(() => {
    getAllUser(currentPage, searchQuery, { professionalType, status, sortBy, sortOrder });
  }, [currentPage]);

  // Debounced search effect - only trigger when user stops typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery !== "") {
        setIsSearching(true);
        setCurrentPage(1); // Reset to first page when searching
        getAllUser(1, searchQuery, { professionalType, status, sortBy, sortOrder });
      } else if (searchQuery === "" && currentPage === 1) {
        // If search is cleared and we're on first page, refresh
        getAllUser(1, "", { professionalType, status, sortBy, sortOrder });
      }
    }, 800); // Increased debounce time for better UX

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Filter and sort changes - immediate effect
  useEffect(() => {
    if (currentPage === 1) {
      // If already on first page, refresh immediately
      getAllUser(1, searchQuery, { professionalType, status, sortBy, sortOrder });
    } else {
      // If on other pages, reset to first page and refresh
      setCurrentPage(1);
    }
  }, [professionalType, status, sortBy, sortOrder]);

  const getAllUser = async (page = 1, search = "", filters: any = {}) => {
    try {
      const params: any = {
        page: page,
        limit: limit,
        search: search,
        status: filters.status,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        roles: [{ id: 4 }] // Local professionals role ID
      };

      // Handle professional type filter
      if (filters.professionalType && filters.professionalType !== "all") {
        // You can add additional filtering logic here if needed
        // For now, we'll use the basic role filter
      }

      // Log the parameters being sent (for debugging)


      const result = await dispatch(getAllUsers(params));

      // Reset search loading state when API call completes
      if (isSearching) {
        setIsSearching(false);
      }

      return result;
    } catch (err) {
      setIsSearching(false);
    }
  };

  const totalPages = Math.ceil(totalCount / limit);

  const handleEditUser = (professional) => {
    // Find the original user data from allUsers array
    const originalUser = allUsers.find(user => user.id === professional.id);
    if (originalUser) {
      setEditingUser(originalUser);
      setShowEditDialog(true);
    }
  };

  const handleEditDialogClose = () => {
    setShowEditDialog(false);
    setEditingUser(null);
  };

  const handleUserUpdated = () => {
    // Refresh the users list after update
    getAllUser(currentPage, searchQuery, { professionalType, status, sortBy, sortOrder });
    handleEditDialogClose();
  };

  const handleSuspendUser = async (userId: string) => {
    try {
      const result = await dispatch(suspendUser({ userId }));
      // Refresh the users list after status update
      getAllUser(currentPage, searchQuery, { professionalType, status, sortBy, sortOrder });

      // Update modal data if it's currently open for this user
      if (showProfessionalDetails && showProfessionalDetails.id === userId) {
        const updatedProfessional = {
          ...showProfessionalDetails,
          status: "suspended"
        };
        setShowProfessionalDetails(updatedProfessional);
      }
    } catch (error) {
    }
  };

  const handleActivateUser = async (userId: string) => {
    try {
      const result = await dispatch(activateUser({ userId }));
      // Refresh the users list after status update
      getAllUser(currentPage, searchQuery, { professionalType, status, sortBy, sortOrder });

      // Update modal data if it's currently open for this user
      if (showProfessionalDetails && showProfessionalDetails.id === userId) {
        const updatedProfessional = {
          ...showProfessionalDetails,
          status: "active"
        };
        setShowProfessionalDetails(updatedProfessional);
      }
    } catch (error) {
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      const result = await dispatch(deleteUser({ userId }));
      // Refresh the users list after deletion
      getAllUser(currentPage, searchQuery, { professionalType, status, sortBy, sortOrder });

      // Update modal data if it's currently open for this user
      if (showProfessionalDetails && showProfessionalDetails.id === userId) {
        setShowProfessionalDetails(null); // Close modal
      }
    } catch (error) {
    }
  };


  const professionals = useMemo(() => {
    return allUsers.map(user => ({
      id: user.id,
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      phone: user.phone || "N/A",
      profession: user.profession || "N/A",
      serviceArea: user.serviceArea || "N/A",
      completedJobs: user.completedJobs || 0,
      totalEarnings: user.totalEarnings || "$0",
      expertise: user.expertise || [],
      status: StatusEnum[parseInt(user.status?.id)] || "unknown",
      verified: parseInt(user.status?.id) === 1,
      location: user.location || "N/A",
      registeredDate: new Date(user.createdAt).toLocaleDateString(),
      rating: user.rating || "N/A",
      favorite: user.favorite, // Include favorite data from API response
    }));
  }, [allUsers]);

  // Update modal data when allUsers changes
  useEffect(() => {
    if (showProfessionalDetails && showProfessionalDetails.id) {
      const updatedUser = allUsers.find(user => user.id === showProfessionalDetails.id);
      if (updatedUser) {
        const updatedStatus = StatusEnum[parseInt(updatedUser.status?.id)] || "unknown";

        // Only update if the status has actually changed
        if (showProfessionalDetails.status !== updatedStatus) {
          const updatedProfessional = {
            id: updatedUser.id,
            name: `${updatedUser.firstName} ${updatedUser.lastName}`,
            email: updatedUser.email,
            phone: updatedUser.phone || "N/A",
            profession: updatedUser.profession || "N/A",
            serviceArea: updatedUser.serviceArea || "N/A",
            completedJobs: updatedUser.completedJobs || 0,
            totalEarnings: updatedUser.totalEarnings || "$0",
            expertise: updatedUser.expertise || [],
            status: updatedStatus,
            verified: parseInt(updatedUser.status?.id) === 1,
            location: updatedUser.location || "N/A",
            registeredDate: new Date(updatedUser.createdAt).toLocaleDateString(),
            rating: updatedUser.rating || "N/A",
          };
          setShowProfessionalDetails(updatedProfessional);
        }
      }
    }
  }, [allUsers]);


  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
      <AdminHeader />
      {isLoading && (
        <div className="fixed left-[50%] top-[40%] z-[1000px]">
          <Spinner color="green" />
        </div>
      )}
      <main className={`flex-1 lg:pl-64 pt-16 ${isLoading ? "opacity-25" : "opacity-100"}`}>
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Local Professionals</h1>
                <p className="text-gray-600 mt-1">Manage and monitor local service professionals</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Export List
                </Button>
              </div>
            </div>

            <Card className="overflow-hidden">
              <div className="p-4 border-b">
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="relative flex-1 sm:min-w-[300px]">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search professionals..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-10 py-2 border rounded-lg"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => {
                            setSearchQuery("");
                            setCurrentPage(1);
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                      {/* Search loading indicator */}
                      {isSearching && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        </div>
                      )}
                    </div>
                    {/* <Button
                      variant="outline"
                      onClick={() => setShowFilters(!showFilters)}
                      className="flex items-center gap-2"
                    >
                      <Filter className="h-5 w-5" />
                      Filters
                    </Button> */}
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">Sort:</span>
                      <select
                        value={`${sortBy}-${sortOrder}`}
                        onChange={(e) => {
                          const [field, order] = e.target.value.split("-");
                          setSortBy(field);
                          setSortOrder(order);
                        }}
                        className="px-4 py-2 border rounded-lg text-sm"
                      >
                        <option value="createdAt-desc">Newest First</option>
                        <option value="createdAt-asc">Oldest First</option>
                        <option value="firstName-asc">Name A-Z</option>
                        <option value="firstName-desc">Name Z-A</option>
                        <option value="email-asc">Email A-Z</option>
                        <option value="email-desc">Email Z-A</option>
                        <option value="lastName-asc">Last Name A-Z</option>
                        <option value="lastName-desc">Last Name Z-A</option>
                      </select>
                    </div>
                  </div>
                </div>

                <AnimatePresence>
                  {showFilters && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mt-4 pt-4 border-t"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-gray-700">Filters</h3>
                        <div className="flex items-center gap-2">
                          {/* Active filters indicator */}
                          {(professionalType !== "all" || status !== 0 || searchQuery || sortBy !== "createdAt" || sortOrder !== "desc") && (
                            <div className="text-xs text-gray-500">
                              Active filters: {[
                                professionalType !== "all" && `Type: ${professionalType}`,
                                status !== 0 && `Status: ${status}`,
                                searchQuery && `Search: "${searchQuery}"`,
                                (sortBy !== "createdAt" || sortOrder !== "desc") && `Sort: ${sortBy} ${sortOrder === "asc" ? "↑" : "↓"}`
                              ].filter(Boolean).join(", ")}
                            </div>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setProfessionalType("all");
                              setStatus(0);
                              setSortBy("createdAt");
                              setSortOrder("desc");
                              setSearchQuery("");
                              setCurrentPage(1);
                            }}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            Clear All Filters
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Professional Type
                          </label>
                          <select
                            value={professionalType}
                            onChange={(e) => {
                              setProfessionalType(e.target.value);
                            }}
                            className="w-full px-4 py-2 border rounded-lg"
                          >
                            <option value="all">All Types</option>
                            <option value="plumber">Plumber</option>
                            <option value="electrician">Electrician</option>
                            <option value="carpenter">Carpenter</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Status
                          </label>
                          <select
                            value={status}
                            onChange={(e) => {
                              setStatus(Number(e.target.value));
                            }}
                            className="w-full px-4 py-2 border rounded-lg"
                          >
                            <option value={0}>All Status</option>
                            <option value={1}>Active</option>
                            <option value={2}>Inactive</option>
                            <option value={3}>Suspended</option>
                            <option value={4}>Deleted</option>
                            <option value={5}>Pending</option>
                            <option value={6}>Approved</option>
                            <option value={7}>Rejected</option>
                            <option value={8}>Completed</option>
                            <option value={9}>Cancelled</option>
                            <option value={10}>Expired</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Sort By
                          </label>
                          <select
                            value={`${sortBy}-${sortOrder}`}
                            onChange={(e) => {
                              const [field, order] = e.target.value.split("-");
                              setSortBy(field);
                              setSortOrder(order);
                            }}
                            className="w-full px-4 py-2 border rounded-lg"
                          >
                            <option value="createdAt-desc">Newest First</option>
                            <option value="createdAt-asc">Oldest First</option>
                            <option value="firstName-asc">Name A-Z</option>
                            <option value="firstName-desc">Name Z-A</option>
                            <option value="email-asc">Email A-Z</option>
                            <option value="email-desc">Email Z-A</option>
                            <option value="lastName-asc">Last Name A-Z</option>
                            <option value="lastName-desc">Last Name Z-A</option>
                          </select>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="overflow-x-auto relative">
                {/* Loading overlay for filter changes */}
                {isLoading && (
                  <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className="text-sm text-gray-600">Loading professionals...</span>
                    </div>
                  </div>
                )}
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedProfessionals(professionals.map(pro => pro.id));
                            } else {
                              setSelectedProfessionals([]);
                            }
                          }}
                        />
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Professional
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rating
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Jobs
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Favorite
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {professionals.length > 0 ? professionals.map((professional) => (
                      <tr key={professional.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            checked={selectedProfessionals.includes(professional.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedProfessionals([...selectedProfessionals, professional.id]);
                              } else {
                                setSelectedProfessionals(selectedProfessionals.filter(id => id !== professional.id));
                              }
                            }}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                              <User className="h-5 w-5 text-gray-400" />
                            </div>
                            <div className="ml-4">
                              <div className="font-medium text-gray-900">{professional.name}</div>
                              <div className="text-sm text-gray-500">{professional.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Briefcase className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-gray-900">{professional.profession}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className={`h-2.5 w-2.5 rounded-full mr-2 ${professional.status === "active" ? "bg-green-500" :
                              professional.status === "suspended" ? "bg-red-500" :
                                professional.status === "pending" ? "bg-yellow-500" :
                                  professional.status === "inactive" ? "bg-gray-500" :
                                    professional.status === "approved" ? "bg-blue-500" :
                                      professional.status === "rejected" ? "bg-red-600" :
                                        professional.status === "completed" ? "bg-green-600" :
                                          professional.status === "cancelled" ? "bg-gray-600" :
                                            professional.status === "expired" ? "bg-orange-500" :
                                              professional.status === "deleted" ? "bg-red-700" :
                                                "bg-blue-500"
                              }`} />
                            <span className="capitalize">{professional.status}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Star className="h-4 w-4 text-yellow-400 fill-current mr-1" />
                            <span>{professional.rating}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                          {professional.completedJobs}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <RealTimeFavoriteButton professional={professional} currentUserFavorite={currentUserFavorite} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowProfessionalDetails(professional)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              View Details
                            </Button>
                            <div className="relative inline-block text-left">
                              <Button
                                variant="ghost"
                                size="sm"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={8} className="px-6 py-12 text-center">
                          <div className="text-gray-500">
                            <User className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                            <p className="text-lg font-medium mb-2">No professionals found</p>
                            <p className="text-sm">
                              {searchQuery || professionalType !== "all" || status !== 0
                                ? "Try adjusting your search or filters"
                                : "No professionals available at the moment"
                              }
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="px-6 py-4 border-t flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">
                    Showing {(currentPage - 1) * limit + 1}-{Math.min(currentPage * limit, totalCount)} of {totalCount} users
                  </span>
                  <span className="text-sm text-gray-500">
                    (Page {currentPage} of {totalPages})
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1 || isLoading}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={currentPage === totalPages || !hasNextPage || isLoading}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </main>
      <br></br>
      <br></br>
      <br></br>
      <br></br>

      {/* Modals */}
      <AnimatePresence>
        {showProfessionalDetails && (
          <ProfessionalDetailModal
            professional={showProfessionalDetails}
            onClose={() => setShowProfessionalDetails(null)}
            onEditUser={handleEditUser}
            onSuspendUser={handleSuspendUser}
            onActivateUser={handleActivateUser}
            onDeleteUser={handleDeleteUser}
            isInternational={false}
          />
        )}
      </AnimatePresence>

      {/* Edit User Dialog */}
      <EditUserDetailModal
        isOpen={showEditDialog}
        onClose={handleEditDialogClose}
        user={editingUser}
        onUserUpdated={handleUserUpdated}
      />
    </div>
  );
};

export default LocalProfessionalsPage2;